import { useState } from 'react';
import { Link } from 'react-router-dom';
import { recorderData } from '@/mocks/mossvox';
import ConsultationForm from '@/components/ConsultationForm';

function RecorderPage() {
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  const faqs = [
    { q: '录音牌适配哪些通话应用？', a: '录音牌适配微信通话、WhatsApp、VoIP、LINE、普通电话等所有通话场景。只要手机或通话设备有音频输出，录音牌即可独立录音转写。' },
    { q: '录音数据是否上传到云端？', a: '所有录音数据仅保存在录音牌本地16GB存储中，不经过任何云端服务器。即使连接电脑导出，也仅在本地操作，确保商务通话的保密性和数据安全。' },
    { q: 'IP67 防护等级的具体含义？', a: 'IP67 表示设备完全防止粉尘进入，并且可在 1 米深的水中浸泡 30 分钟不损坏。适合雨天户外、厨房蒸汽、工地灰尘等恶劣环境使用。' },
    { q: '7 米远距拾音在多大会议室有效？', a: '7 米远距拾音覆盖约 30 平方米的中型会议室，可清晰收录会议桌上所有发言者的声音。对于更大空间，建议将录音牌放置在靠近发言者中心的位置。' },
    { q: 'AI 转写支持的语种有哪些？', a: '录音牌支持 140+ 语种的 AI 自动转写，包括中文、英文、日文、韩文、法文、德文、西班牙文等全球主流语种。转写完成后可导出为 TXT、DOCX、SRT 格式。' },
  ];

  return (
    <>
      {/* Banner */}
      <section className="relative w-full h-[420px] md:h-[520px] overflow-hidden">
        <div className="absolute inset-0 bg-primary-950/85 z-0" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50 z-[1]" />
        <div className="absolute inset-0 z-[1]">
          <img src="/images/banner-bg.jpg" alt="录音牌" className="w-full h-full object-cover object-top opacity-35" />
        </div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-4 md:px-8 text-center">
          <div className="w-full max-w-3xl mx-auto">
            <span className="inline-block px-4 py-1.5 mb-6 text-xs md:text-sm font-medium text-white/80 border border-white/25 rounded-full backdrop-blur-sm">{recorderData.banner.tag}</span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight mb-4">{recorderData.banner.title}</h1>
            <p className="text-lg md:text-2xl text-white/80 font-light">{recorderData.banner.subtitle}</p>
          </div>
        </div>
      </section>

      {/* 产品定位 */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-foreground-900 leading-tight mb-4">AI 转写录音硬件</h2>
          <p className="text-lg md:text-xl text-foreground-500 mb-10">专为通话转写设计 · 本地存储 · 数据安全</p>
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-500"><i className="ri-shield-check-line text-base"></i></div>
              <span>IP67 防护</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-50 text-accent-500"><i className="ri-mic-line text-base"></i></div>
              <span>7 米远距拾音</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-50 text-secondary-500"><i className="ri-battery-charge-line text-base"></i></div>
              <span>70 天待机</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-500"><i className="ri-hard-drive-2-line text-base"></i></div>
              <span>本地存储</span>
            </div>
          </div>
        </div>
      </section>

      {/* 全宽产品展示图 */}
      <section className="bg-background-100">
        <div className="w-full">
          <img src="/images/mossvox/recorder/main.jpg" alt="MossVox 录音牌展示" className="w-full h-[300px] md:h-[500px] object-cover object-center" />
        </div>
      </section>

      {/* 产品展示 & 规格 */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-4 py-1.5 mb-4 text-xs font-medium text-primary-600 bg-primary-50 rounded-full">产品展示</span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-3">轻巧便携，专业录音</h2>
            <p className="text-sm text-foreground-500">一次充电，70天安心待机</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div className="relative">
              <div className="absolute -inset-4 bg-primary-500/5 rounded-3xl" />
              <img src="/images/mossvox/recorder/product.jpg" alt="产品展示" className="relative z-10 w-full rounded-2xl" />
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-4">
              <div className="text-center p-4 bg-white rounded-xl border border-background-200">
                <div className="text-sm font-bold text-foreground-900 mb-1">产品型号</div>
                <div className="text-xs text-foreground-500">MossVox Recorder / XY-R01</div>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-background-200">
                <div className="text-sm font-bold text-foreground-900 mb-1">防护等级</div>
                <div className="text-xs text-foreground-500">IP67 防水防尘</div>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-background-200">
                <div className="text-sm font-bold text-foreground-900 mb-1">拾音距离</div>
                <div className="text-xs text-foreground-500">最远 7 米</div>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-background-200">
                <div className="text-sm font-bold text-foreground-900 mb-1">待机时间</div>
                <div className="text-xs text-foreground-500">约 70 天</div>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-background-200">
                <div className="text-sm font-bold text-foreground-900 mb-1">连续录音</div>
                <div className="text-xs text-foreground-500">约 20 小时</div>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-background-200">
                <div className="text-sm font-bold text-foreground-900 mb-1">存储容量</div>
                <div className="text-xs text-foreground-500">16 GB 内置</div>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-background-200">
                <div className="text-sm font-bold text-foreground-900 mb-1">充电接口</div>
                <div className="text-xs text-foreground-500">Type-C</div>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-background-200">
                <div className="text-sm font-bold text-foreground-900 mb-1">设备重量</div>
                <div className="text-xs text-foreground-500">约 45 g</div>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-background-200">
                <div className="text-sm font-bold text-foreground-900 mb-1">转写语种</div>
                <div className="text-xs text-foreground-500">140+ 语种</div>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-background-200">
                <div className="text-sm font-bold text-foreground-900 mb-1">输出格式</div>
                <div className="text-xs text-foreground-500">TXT / DOCX / SRT</div>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-background-200">
                <div className="text-sm font-bold text-foreground-900 mb-1">适配通话</div>
                <div className="text-xs text-foreground-500">微信/电话/VoIP</div>
              </div>
              <div className="text-center p-4 bg-white rounded-xl border border-background-200">
                <div className="text-sm font-bold text-foreground-900 mb-1">机身尺寸</div>
                <div className="text-xs text-foreground-500">68 × 38 × 12 mm</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 核心数据亮点 */}
      <section className="py-10 md:py-14 px-4 md:px-8 lg:px-12 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-1">IP67</div>
              <div className="text-xs text-foreground-500">防水防尘等级</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-accent-500 mb-1">7m</div>
              <div className="text-xs text-foreground-500">远距高保真拾音</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-secondary-500 mb-1">70天</div>
              <div className="text-xs text-foreground-500">超长待机</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-1">20h</div>
              <div className="text-xs text-foreground-500">连续录音</div>
            </div>
          </div>
        </div>
      </section>

      {/* 核心功能 */}
      <section className="bg-background-50 py-16 md:py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-4 py-1.5 mb-4 text-xs font-medium text-primary-600 bg-primary-50 rounded-full">核心功能</span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900">{recorderData.features.title}</h2>
          </div>

          {/* Feature 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20 md:mb-28">
            <div className="lg:pr-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500 mb-4">
                <i className="ri-shield-check-line text-xl"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">{recorderData.features.items[0].name}</h3>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">{recorderData.features.items[0].desc}</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">完全防尘</span>
                <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">1米防水</span>
                <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">恶劣环境</span>
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl">
              <img src="/images/mossvox/recorder/core-1.jpg" alt={recorderData.features.items[0].name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          </div>

          {/* Feature 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20 md:mb-28">
            <div className="order-2 lg:order-1 overflow-hidden rounded-2xl">
              <img src="/images/mossvox/recorder/core-2.jpg" alt={recorderData.features.items[1].name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="order-1 lg:order-2 lg:pl-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-500 mb-4">
                <i className="ri-mic-line text-xl"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">{recorderData.features.items[1].name}</h3>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">{recorderData.features.items[1].desc}</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">高灵敏度阵列</span>
                <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">会议级拾音</span>
                <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">清晰人声</span>
              </div>
            </div>
          </div>

          {/* Feature 3 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20 md:mb-28">
            <div className="lg:pr-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500 mb-4">
                <i className="ri-file-text-line text-xl"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">{recorderData.features.items[3].name}</h3>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">{recorderData.features.items[3].desc}</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">140+ 语种</span>
                <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">发言稿生成</span>
                <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">结构化导出</span>
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl">
              <img src="/images/mossvox/recorder/core-3.jpg" alt={recorderData.features.items[3].name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          </div>

          {/* Feature 4 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-2 lg:order-1 overflow-hidden rounded-2xl">
              <img src="/images/mossvox/recorder/core-4.jpg" alt={recorderData.features.items[4].name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="order-1 lg:order-2 lg:pl-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-500 mb-4">
                <i className="ri-smartphone-line text-xl"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">{recorderData.features.items[4].name}</h3>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">{recorderData.features.items[4].desc}</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">微信/电话</span>
                <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">WhatsApp</span>
                <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs">VoIP/LINE</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 功能亮点 */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-4 py-1.5 mb-4 text-xs font-medium text-accent-600 bg-accent-50 rounded-full">功能亮点</span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-3">{recorderData.advantages.title}</h2>
            <p className="text-sm md:text-base text-foreground-500">{recorderData.advantages.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            <div className="group bg-white rounded-2xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300">
              <div className="h-44 md:h-52 overflow-hidden">
                <img src="/images/mossvox/recorder/feature-1.jpg" alt={recorderData.features.items[2].name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5 md:p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                    <i className="ri-battery-charge-line text-base"></i>
                  </div>
                  <h4 className="text-sm font-bold text-foreground-900">{recorderData.features.items[2].name}</h4>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed">{recorderData.features.items[2].desc}</p>
              </div>
            </div>
            <div className="group bg-white rounded-2xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300">
              <div className="h-44 md:h-52 overflow-hidden">
                <img src="/images/mossvox/recorder/feature-2.jpg" alt={recorderData.features.items[5].name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5 md:p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-500">
                    <i className="ri-hard-drive-2-line text-base"></i>
                  </div>
                  <h4 className="text-sm font-bold text-foreground-900">{recorderData.features.items[5].name}</h4>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed">{recorderData.features.items[5].desc}</p>
              </div>
            </div>
            <div className="group bg-white rounded-2xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300">
              <div className="h-44 md:h-52 overflow-hidden">
                <img src="/images/mossvox/recorder/feature-3.jpg" alt={recorderData.advantages.items[0].name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5 md:p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500">
                    <i className="ri-lock-line text-base"></i>
                  </div>
                  <h4 className="text-sm font-bold text-foreground-900">{recorderData.advantages.items[0].name}</h4>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed">{recorderData.advantages.items[0].desc}</p>
              </div>
            </div>
            <div className="group bg-white rounded-2xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300">
              <div className="h-44 md:h-52 overflow-hidden">
                <img src="/images/mossvox/recorder/feature-4.jpg" alt={recorderData.advantages.items[3].name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5 md:p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                    <i className="ri-brain-line text-base"></i>
                  </div>
                  <h4 className="text-sm font-bold text-foreground-900">{recorderData.advantages.items[3].name}</h4>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed">{recorderData.advantages.items[3].desc}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 续航与存储对比 */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-3">超长续航 · 海量存储</h2>
          <p className="text-sm text-foreground-500 mb-12 md:mb-16">一次充电，出差一周无需担心电量</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-background-200">
              <div className="text-4xl md:text-5xl font-bold text-primary-500 mb-2">70</div>
              <div className="text-sm text-foreground-600 mb-1">天超长待机</div>
              <div className="text-xs text-foreground-400">超低功耗设计，一次充电安心待机</div>
            </div>
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-background-200">
              <div className="text-4xl md:text-5xl font-bold text-accent-500 mb-2">20</div>
              <div className="text-sm text-foreground-600 mb-1">小时连续录音</div>
              <div className="text-xs text-foreground-400">全天会议不间断录制，重要内容不遗漏</div>
            </div>
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-background-200">
              <div className="text-4xl md:text-5xl font-bold text-secondary-500 mb-2">16<span className="text-xl md:text-2xl">GB</span></div>
              <div className="text-sm text-foreground-600 mb-1">本地存储</div>
              <div className="text-xs text-foreground-400">海量录音文件本地保存，不依赖云端</div>
            </div>
          </div>
        </div>
      </section>

      {/* 数据安全与隐私 */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-4 py-1.5 mb-4 text-xs font-medium text-primary-600 bg-primary-50 rounded-full">安全与隐私</span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-3">企业级数据安全</h2>
            <p className="text-sm md:text-base text-foreground-500">本地存储，不经过任何云端，商务通话绝对保密</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-w-3xl mx-auto">
            <div className="flex flex-col items-center text-center p-6 bg-white rounded-2xl border border-background-200">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-50 text-primary-500 mb-4">
                <i className="ri-lock-line text-2xl"></i>
              </div>
              <h4 className="text-sm font-bold text-foreground-900 mb-2">本地存储</h4>
              <p className="text-xs text-foreground-500 leading-relaxed">数据仅保存在设备 16GB 内置存储，不经过任何云端服务器</p>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-white rounded-2xl border border-background-200">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-accent-50 text-accent-500 mb-4">
                <i className="ri-shield-check-line text-2xl"></i>
              </div>
              <h4 className="text-sm font-bold text-foreground-900 mb-2">离线导出</h4>
              <p className="text-xs text-foreground-500 leading-relaxed">通过 Type-C 接口直连电脑导出，无需联网，确保传输安全</p>
            </div>
            <div className="flex flex-col items-center text-center p-6 bg-white rounded-2xl border border-background-200">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-secondary-50 text-secondary-500 mb-4">
                <i className="ri-file-shield-line text-2xl"></i>
              </div>
              <h4 className="text-sm font-bold text-foreground-900 mb-2">商务保密</h4>
              <p className="text-xs text-foreground-500 leading-relaxed">法务咨询、商务谈判等敏感通话内容绝对安全可控</p>
            </div>
          </div>
        </div>
      </section>

      {/* 技术参数 */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900">{recorderData.specs.title}</h2>
          </div>
          <div className="bg-white rounded-2xl border border-background-200 overflow-hidden">
            <div className="divide-y divide-background-100">
              {recorderData.specs.rows.map((row, idx) => (
                <div key={idx} className={`flex flex-col sm:flex-row py-4 px-6 md:px-8 ${idx % 2 === 0 ? 'bg-background-50/50' : ''}`}>
                  <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wider w-full sm:w-40 flex-shrink-0 mb-1 sm:mb-0">{row.label}</span>
                  <span className="text-sm text-foreground-800">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-4 py-1.5 mb-4 text-xs font-medium text-accent-600 bg-accent-50 rounded-full">常见问题</span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900">FAQs</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-background-200 overflow-hidden">
                <button onClick={() => setFaqOpen(faqOpen === idx ? null : idx)} className="w-full flex items-center justify-between p-5 md:p-6 text-left cursor-pointer">
                  <span className="text-sm font-bold text-foreground-900 pr-4">{faq.q}</span>
                  <i className={`ri-arrow-down-s-line text-lg text-foreground-400 flex-shrink-0 transition-transform duration-300 ${faqOpen === idx ? 'rotate-180' : ''}`}></i>
                </button>
                {faqOpen === idx && (
                  <div className="px-5 md:px-6 pb-5 md:pb-6">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 相关产品 */}
      <section className="py-16 md:py-24 px-4 md:px-8 lg:px-12 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-900">{recorderData.related.title}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-1 gap-6 max-w-lg mx-auto">
            <Link to="/mossvox/subtitle-pro" className="group bg-white rounded-2xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300 cursor-pointer flex">
              <div className="w-40 flex-shrink-0 overflow-hidden">
                <img src="/images/mossvox/subtitle-pro/overview.jpg" alt="字幕精灵 Pro" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5 flex-1 flex flex-col justify-center">
                <h4 className="text-sm font-bold text-foreground-900 mb-1">字幕精灵 Pro</h4>
                <p className="text-xs text-foreground-500">AI悬浮字幕翻译软件，全平台视频会议字幕</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-primary-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">对录音牌感兴趣？</h2>
          <p className="text-white/75 text-sm md:text-base mb-8">立即咨询产品详情或获取企业批量采购报价</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href={recorderData.cta.primary.href} className="px-8 py-3.5 bg-white text-primary-600 text-sm md:text-base font-semibold rounded-full hover:bg-primary-50 transition-all duration-200 whitespace-nowrap cursor-pointer"><i className="ri-customer-service-2-line mr-2"></i>{recorderData.cta.primary.text}</a>
            <Link to={recorderData.cta.secondary.href} className="px-8 py-3.5 bg-primary-600 text-white text-sm md:text-base font-semibold rounded-full hover:bg-primary-700 border border-primary-400 transition-all duration-200 whitespace-nowrap cursor-pointer"><i className="ri-file-download-line mr-2"></i>{recorderData.cta.secondary.text}</Link>
            <Link to={recorderData.cta.tertiary.href} className="px-8 py-3.5 bg-primary-600 text-white text-sm md:text-base font-semibold rounded-full hover:bg-primary-700 border border-primary-400 transition-all duration-200 whitespace-nowrap cursor-pointer"><i className="ri-mail-send-line mr-2"></i>{recorderData.cta.tertiary.text}</Link>
            <a href={recorderData.cta.jd.href} target="_blank" rel="noopener noreferrer" className="px-8 py-3.5 bg-white text-primary-600 text-sm md:text-base font-semibold rounded-full hover:bg-primary-50 transition-all duration-200 whitespace-nowrap cursor-pointer"><i className="ri-shopping-bag-line mr-2"></i>{recorderData.cta.jd.text}</a>
          </div>
        </div>
      </section>

      {/* 咨询表单 */}
      <ConsultationForm badge="在线咨询" title="在线咨询 / 申领样品" subtitle="填写以下信息，我们的商务团队将在 24 小时内与您联系" />
    </>
  );
}

export default RecorderPage;